import en from './en';
import type { AdminUiCopy } from './types';

type ExtendedSections = Pick<
  AdminUiCopy,
  | 'ownerStaff'
  | 'ownerBlogManager'
  | 'ownerBlogEditor'
  | 'superAdminDashboard'
  | 'superAdminSecurity'
  | 'superAdminUsers'
  | 'superAdminDemoRequests'
  | 'superAdminUserDetail'
>;

const itExtendedSections: ExtendedSections = {
  ownerStaff: {
    ownerTools: 'Strumenti proprietario',
    notAuthorized: 'Non autorizzato',
    restrictedBody:
      'Questa area è riservata ai proprietari della piattaforma. Gli strumenti proprietario controllano gli accessi staff e scrivono direttamente nei permessi di produzione.',
    ownerAccessRequired: 'Accesso proprietario richiesto',
    notInOwnerEmails: 'La tua email {email} non è presente in PLATFORM_OWNER_EMAILS.',
    addAndReauth:
      'Aggiungila e autenticati di nuovo con Google/GitHub SSO. In produzione, lo stato proprietario viene valutato da configurazione ambiente e sessione SSO.',
    manageStaffBody:
      'Gestisci gli accessi staff (super admin). Le modifiche sono immediate e registrate per audit. Usa gli strumenti Super Admin per sospendere o riattivare account cliente.',
    grantRevokeTitle: 'Concedi/Rimuovi accesso staff',
    email: 'Email',
    action: 'Azione',
    reason: 'Motivo',
    grantStaff: 'Concedi staff',
    revokeStaff: 'Rimuovi staff',
    working: 'Elaborazione...',
    enterEmail: 'Inserisci una email per continuare',
    provideReason: 'Inserisci un motivo (per i log di audit)',
    currentStaff: 'Staff attuale',
    noStaff: 'Nessun account staff.',
    tableEmail: 'Email',
    tableId: 'ID',
    tableActions: 'Azioni',
    revoke: 'Rimuovi',
    prev: 'Prec.',
    next: 'Succ.',
    pageOf: 'Pagina {page} di {total}',
    recentActions: 'Azioni recenti proprietario',
    noRecentActions: 'Nessuna azione recente.',
    grantDialogTitle: 'Concedere accesso staff?',
    revokeDialogTitle: 'Rimuovere accesso staff?',
    grantDialogDescription: 'Concede accesso completo alla piattaforma a {email}.',
    revokeDialogDescription: "Rimuove l'accesso piattaforma per {email}.",
    confirmGrant: 'Concedi',
    confirmRevoke: 'Rimuovi',
    grantSuccess: 'Accesso staff concesso a {email}',
    revokeSuccess: 'Accesso staff rimosso per {email}',
    failedUpdateStaff: 'Impossibile aggiornare lo staff',
    failedRevoke: 'Impossibile rimuovere',
    revokeReasonPlaceholder: 'Motivo della rimozione',
    revokeReasonHint: 'Usato per audit; sii conciso.',
    ownerManagedUpdateDefault: 'Aggiornamento staff gestito dal proprietario',
    accessNoLongerRequiredDefault: 'Accesso non più necessario',
  },
  ownerBlogManager: {
    title: 'Gestore blog',
    subtitle: 'Crea e gestisci i post del blog',
    accessRestricted: 'Accesso limitato',
    accessBody: 'La gestione blog richiede accesso proprietario o super admin.',
    newPost: 'Nuovo post',
    all: 'Tutti',
    draft: 'Bozza',
    published: 'Pubblicato',
    loadingPosts: 'Caricamento post...',
    noPosts: 'Nessun post al momento. Crea il tuo primo post.',
    createFirstPost: 'Crea il primo post per iniziare a pubblicare aggiornamenti.',
    createPost: 'Crea post',
    tableTitle: 'Titolo',
    tableStatus: 'Stato',
    tableAuthor: 'Autore',
    tableDate: 'Data',
    tableActions: 'Azioni',
    edit: 'Modifica',
    view: 'Apri',
    delete: 'Elimina',
    previous: 'Precedente',
    next: 'Successivo',
    pageOf: 'Pagina {page} di {total}',
    deleteTitle: 'Eliminare il post?',
    deleteBody: 'Questa azione è irreversibile. Il post verrà eliminato definitivamente.',
    cancel: 'Annulla',
    confirmDelete: 'Elimina',
    toastPostDeleted: 'Post eliminato',
    toastDeleteFailed: 'Impossibile eliminare il post',
  },
  ownerBlogEditor: {
    title: 'Editor blog',
    accessRestricted: 'Accesso limitato',
    accessBody: 'La gestione blog richiede accesso proprietario o super admin.',
    editPost: 'Modifica post',
    newPost: 'Nuovo post',
    backToManager: 'Torna al gestore blog',
    saveDraft: 'Salva bozza',
    publish: 'Pubblica',
    saving: 'Salvataggio...',
    publishing: 'Pubblicazione...',
    postTitlePlaceholder: 'Titolo del post',
    status: 'Stato',
    draft: 'Bozza',
    published: 'Pubblicato',
    slug: 'Slug URL',
    slugPlaceholder: 'slug-post',
    excerpt: 'Estratto',
    excerptPlaceholder: 'Breve riepilogo per la lista del blog...',
    featuredImageUrl: 'URL immagine principale',
    tags: 'Tag',
    tagsPlaceholder: 'EUDI, Aggiornamento prodotto, Engineering',
    commaSeparated: 'Separati da virgole',
    authorName: 'Nome autore',
    authorNamePlaceholder: 'Nome autore',
    authorAvatarUrl: 'URL avatar autore',
    remove: 'Rimuovi',
    importMarkdown: 'Importa Markdown',
    markdown: 'Markdown',
    markdownPlaceholder: 'Incolla qui il markdown...',
    preview: 'Anteprima',
    previewPlaceholder: "L'anteprima apparirà qui...",
    cancel: 'Annulla',
    import: 'Importa',
    titleRequired: 'Il titolo e obbligatorio',
    contentRequired: 'Il contenuto e obbligatorio',
    failedLoadPost: 'Impossibile caricare il post',
    recoveredDraft: 'Bozza non salvata recuperata',
    postPublished: 'Post pubblicato',
    draftSaved: 'Bozza salvata',
    draftCreated: 'Bozza creata',
    failedSavePost: 'Impossibile salvare il post',
    imageUploaded: 'Immagine caricata',
    avatarUploaded: 'Avatar caricato',
    uploadFailed: 'Caricamento fallito',
    markdownImported: 'Markdown importato',
    startWritingPlaceholder: 'Inizia a scrivere il tuo post...',
  },
  superAdminDashboard: {
    loadingMetrics: 'Caricamento metriche piattaforma...',
    platformOverview: 'Panoramica piattaforma',
    globalMetrics: 'Metriche globali e analisi prestazioni',
    noMetrics: 'Nessuna metrica disponibile',
    quickActions: 'Azioni rapide',
    platformSecurity: 'Sicurezza piattaforma',
    viewAllUsers: 'Vedi tutti gli utenti',
    demoRequests: 'Richieste demo',
    trialUsers: 'Utenti trial',
    suspendedUsers: 'Utenti sospesi',
    userMetrics: 'Metriche utenti',
    totalUsers: 'Utenti totali',
    activeUsers: 'Utenti attivi',
    activePaidUsers: 'Utenti paganti attivi',
    mrr: 'MRR',
    monthlyRecurringRevenue: 'Ricavo mensile ricorrente',
    ofTotal: '{value}% del totale',
    ofActiveUsers: '{value}% degli utenti attivi',
    planDistribution: 'Distribuzione piani',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    activeUsersWithTotal: 'Utenti attivi{totalSuffix}',
    verificationSessions: 'Sessioni di verifica',
    totalSessions: 'Sessioni totali',
    thisMonth: 'Questo mese',
    lastMonth: 'Mese scorso',
    growth: 'Crescita',
    liveVerifications: 'Verifiche live',
    allTime: 'Totale storico',
    apiKeys: 'Chiavi API',
    totalKeys: 'Chiavi totali',
    activeKeys: 'Chiavi attive',
    liveKeys: 'Chiavi live',
    productionEnvironment: 'Ambiente produzione',
  },
  superAdminSecurity: {
    superAdmin: 'Super Admin',
    platformSecurity: 'Sicurezza piattaforma',
    subtitle: 'Panoramica anomalie cross-merchant per account staff.',
    totalAlerts: 'Alert totali',
    activeAlerts: 'Alert attivi',
    critical: 'Critico',
    topMerchantsLast7d: 'Top merchant (7 gg)',
    filters: 'Filtri',
    severity: 'Severità',
    status: 'Stato',
    merchantIdOptional: 'ID merchant (opzionale)',
    show: 'Mostra',
    any: 'Qualsiasi',
    low: 'Bassa',
    medium: 'Media',
    high: 'Alta',
    active: 'Attivo',
    investigating: 'In analisi',
    resolved: 'Risolto',
    falsePositive: 'Falso positivo',
    refresh: 'Aggiorna',
    securityAlerts: 'Alert sicurezza',
    noAlerts: 'Nessun alert per i filtri correnti.',
    time: 'Ora',
    merchant: 'Merchant',
    type: 'Tipo',
    pageOf: 'Pagina {page} di {total}',
    prev: 'Prec.',
    next: 'Succ.',
  },
  superAdminUsers: {
    superAdmin: 'Super Admin',
    allUsers: 'Tutti gli utenti',
    subtitle: 'Gestione utenti e analisi su tutta la piattaforma',
    totalUsers: 'Utenti totali',
    activeUsers: 'Utenti attivi',
    trialUsers: 'Utenti trial',
    paidUsers: 'Utenti paganti',
    filters: 'Filtri',
    exporting: 'Esportazione...',
    exportCsv: 'Esporta CSV',
    hideFilters: 'Nascondi filtri',
    showFilters: 'Mostra filtri',
    search: 'Ricerca',
    searchPlaceholder: 'Email, nome o azienda...',
    planTier: 'Livello piano',
    allTiers: 'Tutti i livelli',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    status: 'Stato',
    allStatuses: 'Tutti gli stati',
    active: 'Attivo',
    suspended: 'Sospeso',
    loadingUsers: 'Caricamento utenti...',
    noUsersFound: 'Nessun utente trovato',
    tableUser: 'Utente',
    tablePlan: 'Piano',
    tablePlanActive: 'Piano attivo',
    tableUsage: 'Utilizzo',
    tableResources: 'Risorse',
    tableCreated: 'Creato',
    tableActions: 'Azioni',
    inactive: 'Inattivo',
    admin: 'Admin',
    liveThisMonth: 'live questo mese',
    keys: 'chiavi',
    sessions: 'sessioni',
    updated: 'Aggiornato',
    viewSelf: 'Vedi il mio account',
    viewDetails: 'Vedi dettagli',
    usage: 'Utilizzo',
    resources: 'Risorse',
    created: 'Creato',
    showingRange: 'Mostrando {start} - {end} di {total} utenti',
    previous: 'Precedente',
    next: 'Successivo',
    exportStarted: 'Esportazione CSV avviata.',
    unableExport: 'Impossibile esportare gli utenti.',
  },
  superAdminDemoRequests: {
    superAdmin: 'Super Admin',
    demoRequests: 'Richieste demo',
    subtitle: 'Monitora e gestisci richieste demo dai potenziali clienti',
    totalRequests: 'Richieste totali',
    pending: 'In attesa',
    contacted: 'Contattato',
    completed: 'Completato',
    allRequests: 'Tutte le richieste',
    pendingOnly: 'Solo in attesa',
    scheduled: 'Pianificato',
    onboarded: 'Onboarded',
    followUp: 'Follow-up',
    churned: 'Perso',
    showing: 'Mostrate {count} di {total} richieste',
    loading: 'Caricamento richieste demo...',
    noRequestsFound: 'Nessuna richiesta demo trovata',
    noRequestsAll:
      'Nessuna richiesta demo inviata finora. Appariranno qui quando i clienti prenoteranno una demo dalla landing page.',
    noRequestsWithStatus: 'Nessuna richiesta con stato "{status}". Prova un altro filtro.',
    contact: 'Contatto',
    quickInfo: 'Info rapide',
    status: 'Stato',
    submitted: 'Inviata',
    actions: 'Azioni',
    hideDetails: 'Nascondi dettagli',
    showDetails: 'Mostra dettagli',
    noInfo: 'Nessuna info',
    email: 'Email',
    role: 'Ruolo',
    useCase: "Caso d'uso",
    expectedVolume: 'Volume previsto',
    message: 'Messaggio',
    viewFullDetails: 'Vedi dettagli completi',
    pageOf: 'Pagina {page} di {total}',
    justNow: 'Ora',
    minutesAgo: '{count} min fa',
    hoursAgo: '{count} h fa',
    daysAgo: '{count} g fa',
    statusUpdated: 'Stato aggiornato con successo',
    statusUpdateFailed: 'Aggiornamento stato non riuscito',
  },
  superAdminUserDetail: {
    loadingUserDetails: 'Caricamento dettagli utente...',
    backToUsers: 'Torna agli utenti',
    userNotFound: 'Utente non trovato',
    superAdmin: 'Super Admin',
    allUsers: 'Tutti gli utenti',
    editUser: 'Modifica utente',
    accountActions: 'Azioni account',
    accountActionsBody:
      'Gli account sospesi non possono accedere o creare nuove sessioni di verifica.',
    suspending: 'Sospensione...',
    suspendAccount: 'Sospendi account',
    reactivating: 'Riattivazione...',
    reactivateAccount: 'Riattiva account',
    updateUser: 'Aggiorna utente',
    planTier: 'Livello piano',
    status: 'Stato',
    free: 'Gratis',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Sospeso',
    saveChanges: 'Salva modifiche',
    saving: 'Salvataggio...',
    accountInformation: 'Informazioni account',
    email: 'Email',
    name: 'Nome',
    company: 'Azienda',
    emailVerified: 'Email verificata',
    verified: 'Verificato',
    notVerified: 'Non verificato',
    authProvider: 'Provider auth',
    created: 'Creato',
    lastUpdated: 'Ultimo aggiornamento',
    planDetails: 'Dettagli piano',
    currentPlan: 'Piano corrente',
    perMonth: 'mese',
    testVerifications: 'Verifiche test',
    unlimited: 'Illimitato',
    liveVerifications: 'Verifiche live',
    maxTestKeys: 'Max chiavi test',
    maxLiveKeys: 'Max chiavi live',
    suspendTitle: 'Sospendere questo account?',
    suspendDescription:
      'Gli utenti sospesi non possono accedere o creare nuove sessioni di verifica. I dati esistenti vengono mantenuti.',
    suspend: 'Sospendi',
    reactivateTitle: 'Riattivare questo account?',
    reactivateDescription: "La riattivazione ripristina subito l'accesso.",
    reactivate: 'Riattiva',
    totalSessions: 'Sessioni totali',
    completed: 'Completate',
    failed: 'Fallite',
    thisMonth: 'Questo mese',
    apiKeys: 'Chiavi API',
    totalKeys: 'Chiavi totali',
    activeKeys: 'Chiavi attive',
    publicId: 'ID pubblico',
    environment: 'Ambiente',
    lastUsed: 'Ultimo uso',
    live: 'Live',
    test: 'Test',
    active: 'Attiva',
    revoked: 'Revocata',
    never: 'Mai',
    showingRange: 'Mostrate {start} - {end} di {total} chiavi',
    previous: 'Precedente',
    next: 'Successivo',
    pageOf: 'Pagina {page} di {total}',
  },
};

const it: AdminUiCopy = {
  ...en,
  publicHeader: {
    features: 'Funzionalità',
    howItWorks: 'Come funziona',
    pricing: 'Prezzi',
    blog: 'Blog',
    docs: 'Documentazione',
    signIn: 'Accedi',
    getApiKey: 'Ottieni API Key',
  },
  login: {
    ...en.login,
    welcomeBack: 'Bentornato',
    subtitle: 'Accedi alla tua dashboard',
    forgotPassword: 'Password dimenticata?',
    signIn: 'Accedi',
    createAccount: 'Crea account',
  },
  register: {
    ...en.register,
    createAccount: 'Crea account',
    subtitle: 'Inizia con un piano gratuito - nessuna carta richiesta',
    fullName: 'Nome completo',
    companyOptional: 'Azienda (opzionale)',
    password: 'Password',
    passwordStrength: 'Robustezza password',
    creating: 'Creazione account…',
    haveAccount: 'Hai già un account?',
    signIn: 'Accedi',
    teamInviteAcceptedTitle: 'Invito al team accettato',
    teamInviteAcceptedBody: 'Sei stato aggiunto al team. Reindirizzamento alla dashboard team...',
    inviteLockedEmailHint: 'Questa email è collegata al tuo invito team e non può essere modificata.',
  },
  forgotPassword: {
    ...en.forgotPassword,
    checkEmail: 'Controlla la tua e-mail',
    accountExistsNoticeSuffix: 'riceverai a breve un link per reimpostare la password.',
    title: 'Password dimenticata?',
    sendResetLink: 'Invia link di reset',
    rememberPassword: 'Ricordi la password?',
    signIn: 'Accedi',
  },
  resetPassword: {
    ...en.resetPassword,
    successTitle: 'Password reimpostata',
    successBody: 'La tua password è stata reimpostata con successo.',
    successCta: 'Accedi ora',
    title: 'Reimposta password',
    newPassword: 'Nuova password',
    confirmPassword: 'Conferma password',
    reset: 'Reimposta password',
    rememberPassword: 'Ricordi la password?',
    signIn: 'Accedi',
  },
  notFound: {
    pageNotFound: 'Pagina non trovata',
    description: 'La pagina che cerchi non esiste o è stata spostata.',
    goBack: 'Indietro',
    goDashboard: 'Vai alla dashboard',
    supportPrefix: 'Se ritieni che sia un errore, contatta',
  },
  teamManagement: {
    title: 'Team',
    subtitleWithCount: '{current} di {max} membri',
    subtitleDefault: 'Gestisci i membri del team',
    leaveTeam: 'Lascia il team',
    planLockedTitle: 'La gestione team è disponibile nei piani a pagamento',
    planLockedBody: 'Passa a Pro per invitare fino a 3 membri, o Business per fino a 10.',
    viewPlans: 'Vedi piani',
    inviteMemberTitle: 'Invita un membro del team',
    inviteCopiedTitle: 'Link invito copiato negli appunti',
    copyAgain: 'Copia di nuovo',
    inviteLinkHint: 'Condividi questo link con il tuo collega. Scade tra 7 giorni.',
    invitePlaceholder: 'collega@azienda.it',
    roleAdmin: 'Admin',
    roleDeveloper: 'Sviluppatore',
    roleViewer: 'Lettore',
    roleAdminDescription: 'Accesso completo',
    roleDeveloperDescription: 'Gestione chiavi e playground',
    roleViewerDescription: 'Accesso in sola lettura',
    sendInvite: 'Invia invito',
    sendingInvite: 'Invio...',
    limitReachedPrefix: 'Limite membri raggiunto.',
    upgradePlan: 'Aggiorna piano',
    membersTitle: 'Membri',
    owner: 'Proprietario',
    ownerDescription: 'Proprietario account',
    joinedOn: 'Entrato il {date}',
    pending: 'In attesa',
    remove: 'Rimuovi',
    noMembers: 'Nessun membro del team.',
    noMembersCta: 'Invia un invito per iniziare.',
    pendingInvitesTitle: 'Inviti in sospeso',
    expires: 'Scade il {date}',
    copyLink: 'Copia link',
    revoke: 'Revoca',
    leaveDialogTitle: 'Lascia il team',
    leaveDialogBody:
      "Vuoi davvero lasciare questo team? Perderai l'accesso alle risorse di questo account e servirà un nuovo invito per rientrare.",
    leaveDialogConfirm: 'Lascia il team',
    removeDialogTitle: 'Rimuovi membro',
    removeDialogBody: "Rimuovere {email} dal team? Perderà subito l'accesso.",
    removeDialogConfirm: 'Rimuovi',
    revokeDialogTitle: 'Revocare l\'invito?',
    revokeDialogBody: 'L\'invito a {email} verrà annullato. Non potrà unirsi al team.',
    toastInviteCopied: 'Link invito copiato',
    toastCopiedAgain: 'Copiato di nuovo',
    toastInviteRevoked: 'Invito revocato',
    toastInviteRevokeFailed: 'Impossibile revocare invito',
    toastMemberRemoved: 'Membro rimosso',
    toastMemberRemoveFailed: 'Impossibile rimuovere membro',
    toastRoleUpdated: 'Ruolo aggiornato',
    toastRoleUpdateFailed: 'Impossibile aggiornare ruolo',
    toastLeftTeam: 'Hai lasciato il team',
    toastLeaveTeamFailed: 'Impossibile lasciare il team',
    inviteSendFailed: 'Impossibile inviare invito',
  },
  auditLog: {
    title: 'Registro audit',
    subtitle: 'Monitora tutte le attività sul tuo account.',
    searchPlaceholder: 'Cerca per azione, attore, obiettivo, IP, ID richiesta o ID evento',
    allCategories: 'Tutte le categorie',
    categorySecurity: 'Sicurezza',
    categoryApi: 'API',
    categoryDataAccess: 'Accesso dati',
    categoryConfiguration: 'Configurazione',
    allSeverities: 'Tutte le severità',
    severityInfo: 'Info',
    severityWarning: 'Avviso',
    severityError: 'Errore',
    severityCritical: 'Critico',
    clearFilters: 'Cancella filtri',
    loading: 'Caricamento registro audit...',
    empty: 'Nessuna voce di audit trovata.',
    noMatches: 'Nessuna voce corrispondente in questa pagina.',
    timestamp: 'Data e ora',
    eventKey: 'Chiave evento',
    action: 'Azione',
    actor: 'Attore',
    target: 'Obiettivo',
    category: 'Categoria',
    severity: 'Severità',
    source: 'Origine',
    ipAddress: 'Indirizzo IP',
    eventId: 'ID evento',
    requestId: 'ID richiesta',
    userAgent: 'User agent',
    unknown: 'Sconosciuto',
    system: 'Sistema',
    pageSummary: 'Pagina {page} di {totalPages} ({total} voci)',
    previous: 'Precedente',
    next: 'Successivo',
    tapToExpand: 'Tocca per vedere i dettagli',
    details: 'Dettagli',
  },
  inviteAccept: {
    loading: 'Caricamento dettagli invito...',
    noToken: 'Nessun token di invito fornito.',
    invalidInviteDefault: 'Questo invito non è valido, è scaduto o è già stato usato.',
    acceptFailedDefault: "Impossibile accettare l'invito. Potrebbe essere scaduto o revocato.",
    inviteTitle: 'Hai ricevuto un invito',
    inviteSubtitleNeedsLogin: 'Accedi o crea un account per partecipare',
    organization: 'Organizzazione',
    invitedBy: 'Invitato da',
    yourRole: 'Il tuo ruolo',
    role: 'Ruolo',
    acceptInvite: 'Accetta invito',
    joiningTeam: 'Ingresso nel team...',
    successTitle: 'Operazione completata',
    successBodyWithOrg: 'Sei entrato in {organization}. Reindirizzamento...',
    successBodyDefault: 'Sei entrato nel team. Reindirizzamento...',
    errorTitle: 'Impossibile accettare invito',
    goLogin: 'Vai al login',
    signInToAccept: 'Accedi per accettare',
    createAccount: 'Crea un account',
    signIn: 'Accedi',
    needsRegisterTitle: 'Crea il tuo account',
    needsRegisterBodyWithOrg: 'Crea un account con {email} per entrare in {organization}.',
    needsRegisterBodyDefault: 'Crea un account con {email} per entrare nel team.',
    roleAdmin: 'Admin',
    roleDeveloper: 'Sviluppatore',
    roleViewer: 'Lettore',
    roleAdminDescription: 'Accesso completo per gestire chiavi, webhook, fatturazione e team',
    roleDeveloperDescription: 'Crea e gestisci chiavi API, usa il playground',
    roleViewerDescription: 'Accesso in sola lettura a fatturazione e audit log',
  },
  keys: {
    ...en.keys,
    title: 'Chiavi API',
    createKey: '+ Crea chiave',
    emptyAll: 'Nessuna chiave API al momento.',
    rotate: 'Ruota',
    revoke: 'Revoca',
  },
  webhooks: {
    ...en.webhooks,
    createTitle: 'Crea webhook',
    createWebhook: 'Crea webhook',
    existingTitle: 'Webhook esistenti',
    inspectorTitle: 'Ispettore webhook',
  },
  billing: {
    ...en.billing,
    title: 'Piani e fatturazione',
    manageBilling: 'Gestisci fatturazione',
    currentPlan: 'Piano corrente',
    freeFeatures: [
      'Verifiche di test illimitate',
      '100 verifiche live/mese',
      '5 chiavi test, 2 chiavi live',
      'Sandbox base (pass/fail)',
      'Snippet di integrazione (10 linguaggi)',
      'Supporto e-mail',
    ],
    proFeatures: [
      'Verifiche di test illimitate',
      '1.000 verifiche live/mese',
      '10 chiavi test, 5 chiavi live',
      'Fino a 3 membri del team',
      '20+ scenari di test predefiniti',
      'Ispettore webhook',
      'SLA 99,9%',
      'Supporto e-mail',
    ],
    businessFeatures: [
      'Verifiche di test illimitate',
      '10.000 verifiche live/mese',
      '10 chiavi test, 10 chiavi live',
      'Fino a 10 membri del team',
      'Tutto ciò che è incluso in Pro',
      'Crea i tuoi scenari',
      'Report EUDI Readiness (export compliance)',
      'Simulazione paese (DE, FR, ES, IT, NL)',
      'Analisi avanzate',
      'Supporto e-mail prioritario',
    ],
    billingCycle: 'Ciclo di fatturazione',
    contactSales: 'Contatta vendite',
  },
  dashboard: {
    ...en.dashboard,
    overview: 'Panoramica',
    noPriorData: 'Nessun dato precedente',
    noChange: 'Nessuna variazione',
    vsLastPeriod: '{value}% rispetto al periodo precedente',
    last24Hours: 'Ultime 24 ore',
    last30Days: 'Ultimi 30 giorni',
    last7Days: 'Ultimi 7 giorni',
    hour: 'ora',
    day: 'giorno',
    totalVerifications: 'Verifiche totali',
    successRate: 'Tasso di successo',
    activeSessions: 'Sessioni attive',
    currentlyInProgress: 'Attualmente in corso',
    verificationTrend: 'Trend verifiche',
    trackVerificationVolume: 'Monitora il volume di verifiche nel tempo',
    total: 'Totale',
    completed: 'Completate',
    failed: 'Fallite',
    average: 'Media',
    peak: 'Picco',
    perBucket: 'Per {bucket}',
    avgPerBucket: 'Media per {bucket}',
    noDataYet: 'Ancora nessun dato',
    verificationsWillAppear: 'Le verifiche appariranno qui una volta iniziato',
    currentPlan: 'Piano corrente',
    manage: 'Gestisci',
    staff: 'Staff',
    internalAccount: 'Account interno',
    noBillingForStaff: 'Nessuna fatturazione per lo staff',
    liveVerificationsThisMonth: 'Verifiche live (questo mese)',
    upgradeForLiveAccess: 'Esegui l\'upgrade per l\'accesso live',
    nextBillDate: 'Prossima data di fatturazione',
    status: 'Stato',
    daysRemaining: '{days} giorni rimanenti',
    renewingSoon: 'Rinnovo imminente',
    gracePeriod: 'Periodo di grazia',
    daysToUpdatePayment: '{days} giorni per aggiornare il pagamento',
    active: 'Attivo',
    allSystemsOperational: 'Tutti i sistemi operativi',
    advancedAnalytics: 'Analisi avanzate',
    detailedBreakdown: 'Dettaglio per ambiente e chiave API',
    exportCsv: 'Esporta CSV',
    byEnvironment: 'Per ambiente',
    byStatus: 'Per stato',
    live: 'Live',
    test: 'Test',
    topApiKeysThisMonth: 'Top chiavi API (questo mese)',
    advancedAnalyticsUpgrade: 'Analisi avanzate. Esegui l\'upgrade per dettagli ed esportazione CSV.',
    upgrade: 'Upgrade',
    developerResources: 'Risorse per sviluppatori',
    documentation: 'Documentazione',
    typescriptSdk: 'SDK TypeScript',
    onboardingWelcome: 'Benvenuto su WalletGate',
    youreAllSet: 'Tutto pronto!',
    accountReady: 'Il tuo account è pronto. Completa la tua prima verifica in meno di 5 minuti.',
    getStartedIn3Steps: 'Inizia in 3 passaggi',
    getYourApiKey: 'Ottieni la tua chiave API',
    apiKeyReady: 'Hai già una chiave API pronta all\'uso',
    createFirstApiKey: 'Crea la tua prima chiave API per autenticare le richieste',
    viewApiKeys: 'Vedi chiavi API',
    createApiKey: 'Crea chiave API',
    installSdk: 'Installa il SDK',
    addWalletGate: 'Aggiungi WalletGate al tuo progetto con un solo comando',
    copyCommand: 'Copia comando',
    copied: 'Copiato!',
    makeFirstRequest: 'Fai la tua prima richiesta',
    startVerifying: 'Inizia a verificare le identità digitali con poche righe di codice',
    viewDocumentation: 'Vedi documentazione',
    quickStartExample: 'Esempio di avvio rapido',
    copyCode: 'Copia codice',
    apiDocumentation: 'Documentazione API',
    apiDocumentationDesc: 'Riferimento API completo e guide',
    codeExamples: 'Esempi di codice',
    codeExamplesDesc: 'Integrazioni di esempio in più linguaggi',
    support: 'Supporto',
    supportDesc: 'Ottieni aiuto dal nostro team',
    community: 'Community',
    communityDesc: 'Unisciti ad altri sviluppatori',
    complete: 'Completato',
    noPriorRange: 'Nessun dato per il {range} precedente',
    noChangeVsPrior: 'Nessuna variazione rispetto al {range} precedente',
    vsPriorRange: '{value}% rispetto al {range} precedente',
  },
  playground: {
    ...en.playground,
    quickTour: 'Tour rapido',
    quickStart: 'Avvio rapido',
    onboardingConfigureTitle: 'Configura i controlli',
    onboardingConfigureMessage: 'Scegli un modello rapido o aggiungi controlli, poi avvia la verifica.',
    onboardingFlowTitle: 'Segui il flusso live',
    onboardingFlowMessage: 'Segui ogni fase della sessione dal passaggio wallet alla decisione.',
    onboardingShipTitle: 'Rilascia più velocemente',
    onboardingShipMessage: 'Esporta questa esecuzione in JSON o copia il codice di integrazione.',
    configureNow: 'Configura ora',
    checksPass: '{passed}/{total} controlli superati',
    runsUsed: '{used} / {total} esecuzioni utilizzate',
  },
  notifications: {
    title: 'Notifiche',
    subtitle: 'Resta informato su attività dell\'account, avvisi di sicurezza e aggiornamenti del team.',
    searchPlaceholder: 'Cerca notifiche...',
    allCategories: 'Tutte le categorie',
    categorySecurity: 'Sicurezza',
    categoryBilling: 'Fatturazione',
    categoryApi: 'API',
    categoryTeam: 'Team',
    allStatus: 'Tutte',
    unreadOnly: 'Non lette',
    readOnly: 'Lette',
    clearFilters: 'Cancella filtri',
    loading: 'Caricamento notifiche...',
    empty: 'Nessuna notifica al momento. Le attività appariranno qui man mano che si verificano.',
    noMatches: 'Nessuna notifica corrisponde ai tuoi filtri.',
    markRead: 'Segna come letta',
    markAllRead: 'Segna tutto come letto',
    delete: 'Elimina',
    viewAll: 'Vedi tutte le notifiche',
    preferences: 'Preferenze',
    preferencesTitle: 'Preferenze di notifica',
    preferencesSubtitle: 'Scegli come vuoi essere notificato per ogni categoria.',
    inApp: 'In-App',
    email: 'Email',
    savePreferences: 'Salva preferenze',
    savedPreferences: 'Preferenze di notifica salvate.',
    failedSavePreferences: 'Impossibile salvare le preferenze di notifica.',
    category: 'Categoria',
    severity: 'Severità',
    timestamp: 'Ora',
    pageSummary: 'Pagina {page} di {total}',
    previous: 'Precedente',
    next: 'Successivo',
    bellNoNotifications: 'Nessuna nuova notifica',
    bellRecentTitle: 'Notifiche',
    severityInfo: 'Info',
    severityWarning: 'Avviso',
    severityError: 'Errore',
    severityCritical: 'Critico',
    toastMarkedRead: 'Notifica segnata come letta.',
    toastMarkedAllRead: 'Tutte le notifiche segnate come lette.',
    toastDeleted: 'Notifica eliminata.',
    toastDeleteFailed: 'Impossibile eliminare la notifica.',
    toastNewSingle: 'Hai 1 nuova notifica.',
    toastNewMultiple: 'Hai {count} nuove notifiche.',
  },
  ...itExtendedSections,
};

export default it;
